import { useQuery } from '@tanstack/react-query';
import startCase from 'lodash/startCase';

import {
  getRequest,
  paginationMapper,
  resolveQueryString,
} from 'src/utils/request';
import { API } from 'src/reactQuery/Api';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { removeBlankValues } from 'src/utils/component';
import { RequestDataArray } from 'src/types/requestData';

export interface ContractFiltersType {
  job_id: string;
  user_id: string;
  project_id: string;
  status: UCM.ContractStatusType;
  project_type: UCM.ProjectContractType;
}

export const mapContract = (
  contracts: { attributes: UCM.ContractRawType; id: string }[],
): Array<UCM.ContractType> => {
  return contracts.map(
    ({ attributes, id }: { attributes: UCM.ContractRawType; id: string }) => {
      return {
        id,
        wage: attributes.wage,
        uuid: attributes.uuid,
        jobIds: attributes.job_ids,
        firstJobApplicationUuid: attributes.first_job_application_uuid,
        visible: attributes.visible,
        endDate: attributes.end_date,
        jobType: attributes.job_type,
        location: attributes.location,
        daysLeft: attributes.days_left,
        taskType: attributes.task_type,
        startDate: attributes.start_date,
        statusCode: attributes.status_code,
        noticeDate: attributes.notice_date,
        tariffCode: attributes.tariff_code,
        requirement: attributes.requirement,
        maxDuration: attributes.max_duration,
        unpaidLeave: attributes.unpaid_leave,
        projectUuids: attributes.project_uuids,
        monthlyHours: attributes.monthly_hours,
        archiveNumber: attributes.archive_number,
        documentOrigin: attributes.document_origin,
        employmentType: attributes.employment_type,
        contractNumber: attributes.contract_number,
        avgWeeklyHours: attributes.avg_weekly_hours,
        plannedEndDate: attributes.planned_end_date,
        signatureStatus: attributes.signature_status,
        projectTypeCode: attributes.project_type_code,
        latestStartDate: attributes.latest_start_date,
        userSigningDate: attributes.user_signing_date,
        plannedStartDate: attributes.planned_start_date,
        companySigningDate: attributes.company_signing_date,
        cancellationRequestedDate: attributes.cancellation_requested_date,
        cancellationSource: attributes.cancellation_source,
        user: {
          id: attributes.user.id,
          uuid: attributes.user.uuid,
          email: attributes.user.email,
          firstName: attributes.user.first_name,
          lastName: attributes.user.last_name,
        },
        company: {
          name: attributes.company.name,
          uuid: attributes.company.uuid,
        },
        certificate: {
          code: attributes.certificate.code,
          title: attributes.certificate.title,
        },
        showReinstatePhysical: attributes.show_reinstate_physical,
        documentType: attributes.document_type,
      };
    },
  );
};

const getContracts = async (
  filters: ContractFiltersType,
  dynamicFilters: Array<UCM.DynamicFiltersType>,
  params: Record<string, string | number>,
) => {
  const signatureStatuses = ['not_signed', 'user_signed', 'completely_signed'];
  const isSignatureStatus = signatureStatuses.includes(filters.status);
  const statusKey = isSignatureStatus ? 'Signature status' : 'Status';

  const quickFilters = {
    ...removeBlankValues(
      Object.fromEntries(
        Object.entries(filters).filter(
          ([key]) => key !== 'status' && key !== 'project_type',
        ),
      ),
    ),
  };

  const updatedFilters = [
    ...(filters.status !== 'all'
      ? [
          {
            name: statusKey,
            operator: 'eq',
            value: filters.status.replace('_', ' '),
          },
        ]
      : []),
    ...(filters.project_type !== 'all'
      ? [
          {
            name: 'Project type',
            operator: 'eq',
            value: filters.project_type,
          },
        ]
      : []),
    ...Object.entries(quickFilters).map(([key, value]) => ({
      name: startCase(key).replace(/\bId\b/g, 'ID'),
      operator: 'eq',
      value: value!,
    })),
    ...dynamicFilters,
  ];

  const query = resolveQueryString([updatedFilters], params);

  const {
    data: { data, meta },
  } = await getRequest<RequestDataArray<UCM.ContractRawType, UCM.MetaRawType>>(
    `${API.GET.getContracts}${query}`,
  );

  return {
    meta: paginationMapper(meta),
    data: mapContract(data),
  };
};

export const useGetContracts = (
  params: Record<string, string | number>,
  filters: ContractFiltersType,
  dynamicFilters: Array<UCM.DynamicFiltersType> = [],
  config: { enabled?: boolean } = { enabled: true },
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.CONTRACTS, filters, dynamicFilters, params],
    queryFn: () => getContracts(filters, dynamicFilters, params),
    staleTime: 1000 * 60 * 5, // Dashboards refreshed after every 5 minutes automatically
    ...config,
  });
};
